import { AppsFlyer } from 'appsflyer-capacitor-plugin';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { isNative, nativePlatform, hasSeenNativeIntro } from './platform';

/**
 * AppsFlyer attribution layer (native only). Attributes installs to ad
 * campaigns and reports the signup/paywall funnel back to the ad networks so
 * they can optimize toward users who actually convert.
 *
 * REVENUE IS NEVER LOGGED FROM HERE. Store purchases reach AppsFlyer through
 * RevenueCat's server-side integration, keyed on the AppsFlyer ID that
 * lib/revenueCat.ts attaches to the RevenueCat customer. A client-side copy of
 * a purchase would double-count revenue in AppsFlyer AND in every ad-network
 * postback derived from it, which is not reversible after the fact.
 */

/** Seconds the SDK holds the install event waiting for the ATT answer. The
    prompt sits early in onboarding (~20-30s in) so this clears it with margin;
    it also resolves instantly on later launches, where ATT is already settled. */
const ATT_WAIT_SECONDS = 90;

let initPromise: Promise<void> | null = null;
let attStatus: string | null = null;

/**
 * Start the SDK. Called once at module scope from index.tsx — earlier than a
 * React effect could, and immune to StrictMode's double-invoke. Fire and
 * forget: attribution must never delay or break app startup, so a missing key
 * or a failed init degrades to a silent no-op rather than throwing at boot.
 */
export const initAppsFlyer = (): void => {
  if (!isNative() || initPromise) return;

  const devKey = process.env.REACT_APP_AF_DEV_KEY;
  const appID = process.env.REACT_APP_AF_APP_ID;
  if (!devKey) return;

  initPromise = AppsFlyer.initSDK({
    devKey,
    appID: appID ?? '',
    isDebug: process.env.REACT_APP_AF_DEBUG === 'true',
    waitForATTUserAuthorization: ATT_WAIT_SECONDS,
  })
    .then(() => undefined)
    .catch(() => {
      initPromise = null;
    });

  // Launches that never render the onboarding primer (returning users,
  // reinstalls, post-update) would otherwise stall the install event for the
  // full wait window and then ship it without an IDFA. Prompting here settles
  // ATT inside the window instead; iOS shows the alert at most once per
  // install, so a user who already answered is never asked again.
  if (hasSeenNativeIntro()) void requestTrackingConsent();
};

/**
 * Show Apple's App Tracking Transparency prompt (iOS only; a no-op elsewhere).
 * Idempotent by construction — iOS presents the system alert at most once per
 * install and returns the settled status thereafter.
 */
export const requestTrackingConsent = async (): Promise<void> => {
  if (nativePlatform() !== 'ios') return;
  try {
    const { status } = await AppTrackingTransparency.requestPermission();
    attStatus = status;
  } catch {
    // Prompt unavailable — attribution falls back to SKAdNetwork
  }
};

/**
 * The ATT status for RevenueCat's $attConsentStatus subscriber attribute.
 * Reads the cached answer, and on iOS falls back to querying the settled
 * status (never prompting) so the value is correct even on a launch where
 * nothing asked. Null on Android and web, where the attribute doesn't apply.
 */
export const resolveAttConsentStatus = async (): Promise<string | null> => {
  if (attStatus) return attStatus;
  if (nativePlatform() !== 'ios') return null;
  try {
    const { status } = await AppTrackingTransparency.getStatus();
    attStatus = status;
    return status;
  } catch {
    return null;
  }
};

/**
 * Log a funnel event. Fire and forget — an attribution event is never worth
 * surfacing an error or delaying a user flow for, so this returns void and
 * cannot be awaited into a critical path.
 *
 * NEVER use this for af_purchase, af_subscribe, af_renew, af_start_trial or
 * af_ad_revenue, and never pass af_revenue/af_currency/af_price in the values.
 * Revenue arrives server-side from RevenueCat; see the module doc block.
 */
export const logAppsFlyerEvent = (
  eventName: string,
  eventValue?: Record<string, unknown>
): void => {
  if (!isNative() || !initPromise) return;
  void initPromise
    .then(() => AppsFlyer.logEvent({ eventName, eventValue: eventValue ?? {} }))
    .catch(() => {
      // Attribution is best-effort
    });
};

// --- Signup method ---------------------------------------------------------
// af_registration_method is logged on the first authenticated visit, but the
// method is only known at sign-up — and native social sign-up hard-reloads the
// WebView in between, so a module variable can't survive the gap.

const SIGNUP_METHOD_KEY = 'insight:signup-method';

export const rememberSignupMethod = (method: string): void => {
  try {
    localStorage.setItem(SIGNUP_METHOD_KEY, method);
  } catch {
    // Storage unavailable — the event just goes out without the method
  }
};

/** Reads and clears the stored method, so it can't label a later account. */
export const takeSignupMethod = (): string | null => {
  try {
    const method = localStorage.getItem(SIGNUP_METHOD_KEY);
    localStorage.removeItem(SIGNUP_METHOD_KEY);
    return method;
  } catch {
    return null;
  }
};

/**
 * Pin AppsFlyer's records to our better-auth user id — the same value used as
 * the RevenueCat appUserID and the PostHog distinct id, so raw attribution
 * data joins to product analytics and to the membership row on one key.
 */
export const setAppsFlyerCustomerUserId = async (userId: string): Promise<void> => {
  if (!isNative() || !initPromise) return;
  try {
    await initPromise;
    await AppsFlyer.setCustomerUserId({ cuid: userId });
  } catch {
    // Attribution is best-effort
  }
};

/**
 * The AppsFlyer device id, for RevenueCat's $appsflyerId subscriber attribute
 * (the join key its server-side integration attributes purchases with).
 * Awaits init internally, so callers can't read it before the SDK is ready.
 */
export const getAppsFlyerUid = async (): Promise<string | null> => {
  if (!isNative() || !initPromise) return null;
  try {
    await initPromise;
    const { uid } = await AppsFlyer.getAppsFlyerUID();
    return uid ?? null;
  } catch {
    return null;
  }
};
